import AddIcon from '@mui/icons-material/Add'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  useEnquiries,
  useLeadSources,
  useOutlets,
  useSaveEnquiry,
  useEventTypes,
  useServiceTypes,
} from '@/api/hooks'
import type { CustomerSummary } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { CustomerPicker } from '@/components/CustomerPicker'
import { NewCustomerDialog } from '@/pages/CustomersPage'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMobile,
  formatMoney,
  formatNumber,
  humanise,
  isoDate,
  PageHeader,
  StatusChip,
} from '@/components/common'

const STATUSES = ['NEW', 'CONTACTED', 'QUOTATION_SENT', 'CONFIRMED', 'LOST', 'CANCELLED']

export function EnquiriesPage() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const [status, setStatus] = useState('')
  const [serviceType, setServiceType] = useState('')
  // The lines of business this company sells, for narrowing the list.
  const { data: filterServices } = useServiceTypes()
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading, error } = useEnquiries({
    status: status || undefined,
    serviceType: serviceType || undefined,
    q: q || undefined,
    page,
    size,
  })

  return (
    <>
      <PageHeader
        title="Enquiries"
        subtitle="Every lead from first contact through to a confirmed booking"
        actions={
          canWrite('enquiry') ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              New enquiry
            </Button>
          ) : undefined
        }
      />

      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search enquiry number"
              value={q}
              onChange={(event) => {
                setQ(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Service"
              value={serviceType}
              onChange={(event) => {
                setServiceType(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All services</MenuItem>
              {(filterServices ?? []).map((service) => (
                <MenuItem key={service.code} value={service.code}>
                  {service.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.content.length === 0 ? (
          <EmptyState
            message="No enquiries match these filters."
            hint="Clear the filters, or raise a new enquiry to get started."
          />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Enquiry</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Guests</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Probability</TableCell>
                  <TableCell align="right">Weighted value</TableCell>
                  <TableCell>Next follow-up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((enquiry) => (
                  <TableRow key={enquiry.id} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/enquiries/${enquiry.id}`} underline="hover">
                        {enquiry.enquiryNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{enquiry.customerName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatMobile(enquiry.customerMobile)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{humanise(enquiry.eventType)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {humanise(enquiry.serviceType)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(enquiry.eventDate)}</TableCell>
                    <TableCell align="right">{formatNumber(enquiry.expectedGuestCount)}</TableCell>
                    <TableCell>
                      <StatusChip status={enquiry.status} />
                    </TableCell>
                    <TableCell align="right">{enquiry.probability}%</TableCell>
                    <TableCell align="right">{formatMoney(enquiry.estimatedValue)}</TableCell>
                    <TableCell>{formatDateTime(enquiry.nextFollowupAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </Card>

      <NewEnquiryDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  )
}

function NewEnquiryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const save = useSaveEnquiry()
  const { data: outlets } = useOutlets()
  // What this business actually sells, and which of those ask what occasion it
  // is for. Both used to be lists in this file.
  const { data: services } = useServiceTypes()
  const bookableServices = (services ?? []).filter((service) => service.bookable)
  const { data: leadSources } = useLeadSources()

  const [form, setForm] = useState({
    outletId: '',
    serviceType: 'BANQUET',
    eventType: '',
    eventDate: isoDate(0),
    expectedGuestCount: 200,
    budgetAmount: '',
    leadSourceId: '',
    specialRequirements: '',
  })
  // An enquiry is usually from someone already on the books. Picking them keeps
  // the enquiry against their history instead of raising a second record of the
  // same person, which is what typing the name again does.
  const [existing, setExisting] = useState<CustomerSummary | null>(null)
  const [addingCustomer, setAddingCustomer] = useState(false)

  // Read after the form exists, not before it: a counter sale asks for no
  // occasion, and the service chosen on the form decides that. Asked of the
  // master rather than of a flag, so the question is only put where there are
  // answers to choose from.
  const { data: occasions } = useEventTypes({ serviceType: form.serviceType || undefined })
  const eventTypeRequired = (occasions ?? []).length > 0

  // An occasion belongs to the line of business it was chosen under, so a
  // change of service cannot carry it across: the same code rarely means the
  // same thing twice, and usually is not offered there at all. Left behind, it
  // would sit in the field looking chosen and be refused on save.
  useEffect(() => {
    if (form.eventType && !(occasions ?? []).some((one) => one.code === form.eventType)) {
      setForm((current) => ({ ...current, eventType: '' }))
    }
  }, [occasions, form.eventType])

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function submit() {
    await save.mutateAsync({
      body: {
        // Always a record: found on the list, or added from this form. There is
        // no third way of naming somebody any more.
        customerId: existing?.id,
        outletId: form.outletId || undefined,
        serviceType: form.serviceType,
        eventType: eventTypeRequired ? form.eventType : undefined,
        eventDate: form.eventDate,
        expectedGuestCount: Number(form.expectedGuestCount),
        budgetAmount: form.budgetAmount ? Number(form.budgetAmount) : undefined,
        leadSourceId: form.leadSourceId || undefined,
        specialRequirements: form.specialRequirements || undefined,
      },
    })
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Entered here and chosen straight away, so nothing typed on this form
          is lost to a trip to the customers screen. */}
      <NewCustomerDialog
        open={addingCustomer}
        onCreated={(customer) => {
          setExisting({
            id: customer.id,
            customerCode: customer.customerCode,
            name: customer.name,
            mobile: customer.mobile,
            email: customer.email,
          } as CustomerSummary)
        }}
        onClose={() => setAddingCustomer(false)}
      />
      <DialogTitle>New enquiry</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ flexGrow: 1 }}>
            <CustomerPicker
              value={existing}
              onChange={setExisting}
              label="Customer"
              required
              helperText="Search by name, mobile, email or customer code — or add them"
            />
              </Box>
              {/* Somebody ringing for the first time is not on the list yet,
                  and going to the customers screen to add them loses whatever
                  has been typed here. */}
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                sx={{ mt: 1, whiteSpace: 'nowrap' }}
                onClick={() => setAddingCustomer(true)}
              >
                Add customer
              </Button>
            </Stack>
          </Grid>
          {/* Who it is for is settled above, by finding them or adding them.
              Their name, mobile and email were asked for again here and typed
              again on every call - which is how one person ended up on the
              books three times, and how an enquiry ended up against a record
              that was not theirs. */}
          <Grid item xs={12}>
            {existing ? (
              <Alert severity="success" onClose={() => setExisting(null)}>
                Raising this against {existing.name} ({existing.customerCode}) ·{' '}
                {formatMobile(existing.mobile)}. Close this to choose somebody else.
              </Alert>
            ) : (
              <Alert severity="info">
                Find the customer above, or add them. The enquiry is raised against their record.
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Outlet"
              value={form.outletId}
              onChange={(event) => update('outletId', event.target.value)}
              fullWidth
            >
              <MenuItem value="">Not decided</MenuItem>
              {outlets?.map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Service type"
              value={form.serviceType}
              onChange={(event) => update('serviceType', event.target.value)}
              fullWidth
            >
              {bookableServices.map((service) => (
                <MenuItem key={service.code} value={service.code}>
                  {service.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* A wedding has an occasion; a counter sale has none. Which lines of
              business ask is set against the service, not decided here. */}
          {eventTypeRequired ? (
            <Grid item xs={12} md={4}>
              <TextField
                select
                required
                label="Event type"
                value={form.eventType}
                onChange={(event) => update('eventType', event.target.value)}
                fullWidth
              >
                {(occasions ?? []).map((occasion) => (
                  <MenuItem key={occasion.code} value={occasion.code}>
                    {occasion.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : null}
          <Grid item xs={12} md={4}>
            <TextField
              label="Event date"
              type="date"
              value={form.eventDate}
              onChange={(event) => update('eventDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Expected guests"
              type="number"
              value={form.expectedGuestCount}
              onChange={(event) => update('expectedGuestCount', Number(event.target.value))}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Budget"
              type="number"
              value={form.budgetAmount}
              onChange={(event) => update('budgetAmount', event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Lead source"
              value={form.leadSourceId}
              onChange={(event) => update('leadSourceId', event.target.value)}
              fullWidth
            >
              <MenuItem value="">Not recorded</MenuItem>
              {leadSources?.map((source) => (
                <MenuItem key={source.id} value={source.id}>
                  {source.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Special requirements"
              value={form.specialRequirements}
              onChange={(event) => update('specialRequirements', event.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          // Nobody to raise it against is the one thing that stops it now.
          disabled={save.isPending || !existing || !form.eventDate}
        >
          {save.isPending ? 'Saving…' : 'Create enquiry'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
